import { FC, Fragment, useCallback, useEffect, useState } from "react";
import cx from "classnames";
import dynamic from "next/dynamic";
import { CgSpinner } from "react-icons/cg";
import * as CheckMark from "@modules/tasks/assets/checkmark.json";
import { Transition } from "@headlessui/react";
import { useChangeTaskStatus } from "@modules/tasks/hooks";
import moment from "moment";
import { getCTADisplayName } from "@modules/tasks/utils.frontend";
import { HiOutlineClock } from "react-icons/hi";
import useTasksStore from "@modules/tasks/state";
import { themeTextColors } from "@common/constants";
import React from "react";
import { Button, Typography, Tooltip } from "@earned/core";
import { useBreakpoints } from "@common/hooks";
import { CTA, ProductTopic } from "@modules/tasks/types";
import { getTaskDueDateDiff } from "@modules/tasks/utils";

const Player = dynamic(
  () => import("@lottiefiles/react-lottie-player").then((m) => m.Player),
  { ssr: false }
);

interface TaskItemProps {
  name: string;
  taskId: string;
  isAdvisor: boolean;
  onAction: (callToAction: string) => Promise<void>;
  onEditTask: (taskId: string) => void;
  description?: string;
  callToAction?: string | null;
  isCompleted?: boolean;
  estimatedCompletionTime?: number | null;
  attachedNote?: string | null;
  disabledCallToActionTooltip?: string;
  ctaText?: string | null;
  productTopic?: ProductTopic;
  topicIcon?: React.ReactNode;
  dueDate?: Date | null;
}

const TaskItem: FC<TaskItemProps> = ({
  name,
  description,
  callToAction,
  taskId,
  onAction,
  onEditTask,
  isCompleted: isCompletedTask,
  isAdvisor,
  estimatedCompletionTime,
  attachedNote,
  disabledCallToActionTooltip,
  ctaText,
  productTopic,
  topicIcon,
  dueDate,
}) => {
  const [isCallToActionInProgress, setIsCallToActionInProgress] =
    useState(false);
  const [isCompleted, setIsCompleted] = useState(false);
  const [runningAnimation, setRunningAnimation] = useState(false);

  const { changeStatusToCompleted, changeStatusToInProgress } =
    useChangeTaskStatus();

  const { remoteCompleteTaskId, setRemoteCompleteTaskId } = useTasksStore();

  const onCompleted = useCallback(
    (taskId: string) => {
      setIsCompleted(true);
      setRunningAnimation(true);

      setTimeout(async () => {
        setIsCompleted(false);
        await changeStatusToCompleted(taskId, 700);
        setRunningAnimation(false);
        setRemoteCompleteTaskId(null); // just incase.
      }, 1200);
    },
    [changeStatusToCompleted, setRemoteCompleteTaskId]
  );

  useEffect(() => {
    if (remoteCompleteTaskId && taskId && remoteCompleteTaskId === taskId) {
      onCompleted(remoteCompleteTaskId);
    }
  }, [remoteCompleteTaskId, onCompleted, taskId]);

  const onRestore = useCallback(
    (taskId: string) => {
      changeStatusToInProgress(taskId);
    },
    [changeStatusToInProgress]
  );

  /* Attached Note Hooks Start */

  const [attachedNoteContent, setAttachedNoteContent] = useState(
    attachedNote || ""
  );
  useEffect(() => {
    if (attachedNote !== attachedNoteContent) {
      setAttachedNoteContent(attachedNote || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedNote]);

  /* Attached Note Hooks End */
  const onCompleteOrRestore = useCallback(() => {
    if (!isCompletedTask) {
      onCompleted(taskId);
    } else {
      onRestore(taskId);
    }
  }, [isCompletedTask, taskId, onCompleted, onRestore]);

  const onCTA = useCallback(async () => {
    if (!disabledCallToActionTooltip) {
      try {
        setIsCallToActionInProgress(true);
        await onAction(taskId);
      } finally {
        setIsCallToActionInProgress(false);
      }
    }
  }, [
    setIsCallToActionInProgress,
    onAction,
    disabledCallToActionTooltip,
    taskId,
  ]);

  const { isMobile } = useBreakpoints();

  const CallToActionButton = (
    <Button
      variant={!!disabledCallToActionTooltip ? "disabled" : "primary"}
      size={isMobile ? "lg" : "md"}
      fullWidth={true}
      disabled={isCallToActionInProgress}
      data-testid="call to action button"
      onTouchEnd={onCTA}
      onClick={onCTA}
    >
      {isCallToActionInProgress ? (
        <div className="flex flex-row items-center">
          <CgSpinner className="animate-spin" size={24} />
        </div>
      ) : (
        getCTADisplayName(callToAction as string, ctaText)
      )}
    </Button>
  );

  const { dueDateDiff, isDueDatePast } = getTaskDueDateDiff(dueDate);
  return (
    <>
      <Transition as={Fragment} show={isCompleted}>
        <Transition.Child
          enter="opacity-1 transition-all ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-1"
          leave="transition-all -translate-x-full ease-in-out duration-700"
          leaveFrom="translate-x-full opacity-1"
          leaveTo="-translate-x-full opacity-0"
        >
          <div
            className={
              "flex max-h-[200px] flex-row rounded-lg bg-green-200 px-[15px] pb-[20px] pt-[15px] text-white shadow-md"
            }
          >
            <div className="flex flex-row items-center">
              <Player
                autoplay
                keepLastFrame
                src={CheckMark}
                style={{ height: "85px", width: "85px" }}
              />
            </div>
            <div className="flex flex-col justify-center">
              <Typography variant="title-md" color="light" weight="medium">
                Congrats: Task Completed
              </Typography>
              <Typography variant="body" color="light" weight="medium">
                {name}
              </Typography>
            </div>
          </div>
        </Transition.Child>
      </Transition>

      <div
        className={cx(
          `flex min-h-[90px] max-w-[1060px] flex-row justify-between rounded-lg bg-white p-6 shadow-md sm:p-4`,
          {
            "select-none": !isCompletedTask,
            "hidden": runningAnimation,
          }
        )}
      >
        <div className="flex flex-1 flex-col justify-between gap-6 sm:flex-row sm:gap-16">
          <div className="flex flex-1 flex-col justify-center gap-4">
            <div className="flex flex-col gap-6 sm:gap-4">
              <div className="flex flex-col gap-4">
                <div className="flex flex-row items-start justify-between gap-4 md:justify-start">
                  <div className="flex flex-row gap-3 sm:items-center">
                    {topicIcon}
                    <div className="flex flex-col">
                      <Typography
                        variant="body"
                        weight="medium"
                        color="primary"
                      >
                        {name}
                      </Typography>
                      {productTopic && (
                        <Typography
                          variant="body-sm"
                          color="primary"
                          className="hidden sm:block"
                        >
                          {productTopic}
                        </Typography>
                      )}
                    </div>
                  </div>

                  {!isCompletedTask && estimatedCompletionTime && (
                    <div className="flex flex-none flex-row items-center gap-2">
                      <HiOutlineClock
                        color={themeTextColors.secondary}
                        size={24}
                      />
                      <Typography variant="body-sm" color="secondary">
                        {estimatedCompletionTime} min
                      </Typography>
                    </div>
                  )}
                </div>

                <Typography variant="body" color="primary">
                  {description}
                </Typography>
              </div>
              {!isCompletedTask && dueDateDiff !== null ? (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <div
                    className={cx(
                      "w-fit rounded-[4px] px-2 py-1",
                      dueDateDiff >= 3 && "bg-green-50",
                      dueDateDiff >= 0 &&
                        dueDateDiff <= 3 &&
                        "bg-warning-light",
                      isDueDatePast && "bg-red-50"
                    )}
                  >
                    <Typography
                      variant="body-sm"
                      color="black"
                      className="inline-block whitespace-nowrap"
                    >
                      Recommended Due Date:{" "}
                      {isDueDatePast
                        ? "Past Due"
                        : moment(dueDate).format("MM/DD/YYYY")}
                    </Typography>
                  </div>
                  <Typography variant="body-sm" color="secondary-gray">
                    Please complete this task on time to keep your scheduled
                    advisory meeting.
                  </Typography>
                </div>
              ) : null}
            </div>
            {isCompletedTask && attachedNoteContent && (
              <Typography variant="body" color="primary">
                {attachedNoteContent}
              </Typography>
            )}
          </div>
          <div className="flex min-w-[100px] flex-none flex-col items-end justify-center gap-2 md:min-w-[200px]">
            {isAdvisor && (
              <>
                <div className="w-full">
                  <Button
                    size={isMobile ? "lg" : "md"}
                    variant="tertiary-dark"
                    onTouchEnd={() => onEditTask(taskId)}
                    onClick={() => onEditTask(taskId)}
                    fullWidth={true}
                  >
                    Edit this task
                  </Button>
                </div>
                {isCompletedTask && callToAction == CTA.OPEN_WIZARD ? (
                  <div className="w-full">
                    <Button
                      size={isMobile ? "lg" : "md"}
                      variant="tertiary-dark"
                      onClick={onCTA}
                      fullWidth={true}
                    >
                      Open Wizard
                    </Button>
                  </div>
                ) : null}
              </>
            )}
            {callToAction &&
              callToAction != "Task" &&
              !isCompletedTask &&
              (!!disabledCallToActionTooltip ? (
                <Tooltip
                  placement="left"
                  description={disabledCallToActionTooltip ?? ""}
                >
                  {CallToActionButton}
                </Tooltip>
              ) : (
                CallToActionButton
              ))}
            {(callToAction !== CTA.OPEN_WIZARD || isCompletedTask) && (
              <div className="w-full">
                <Button
                  variant={
                    isCompletedTask ||
                    isMobile ||
                    !callToAction ||
                    callToAction == "Task"
                      ? "secondary"
                      : "tertiary"
                  }
                  size={isMobile ? "lg" : "md"}
                  fullWidth={true}
                  onTouchEnd={onCompleteOrRestore}
                  onClick={onCompleteOrRestore}
                >
                  <div className="whitespace-nowrap">
                    {isCompletedTask
                      ? "Restore to active"
                      : !!attachedNoteContent
                      ? "Complete with note"
                      : "Mark as Completed"}
                  </div>
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default TaskItem;
